import React from "react";
import { createRoot } from "react-dom/client";
import ReportLayout from "../layouts/ReportLayout";
import { jsPDF } from "jspdf";
import { applyPlugin } from "jspdf-autotable";
import { format } from "date-fns";

applyPlugin(jsPDF);

/**
 * Generate PDF for report using the ReportLayout component
 * This follows the same pattern as Invoice/Payout/Manifest PDFs
 */

/**
 * Format currency value
 */
const formatCurrency = (value) => {
  if (value === null || value === undefined || isNaN(value)) return "$0.00";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(value);
};

/**
 * Format number with commas
 */
const formatNumber = (value) => {
  if (value === null || value === undefined || isNaN(value)) return "0";
  return value.toLocaleString();
};

/**
 * Format date range based on type
 */
const formatDateRange = (reportMetadata) => {
  if (
    !reportMetadata?.dateRange?.startDate ||
    !reportMetadata?.dateRange?.endDate
  ) {
    return "N/A";
  }

  const startDate = new Date(reportMetadata.dateRange.startDate);
  const endDate = new Date(reportMetadata.dateRange.endDate);

  switch (reportMetadata.dateRangeType) {
    case "custom":
      return `${format(startDate, "MMM dd, yyyy")} - ${format(
        endDate,
        "MMM dd, yyyy"
      )}`;
    case "monthly":
      return format(startDate, "MMMM yyyy");
    case "quarterly":
      const quarter = Math.ceil((startDate.getMonth() + 1) / 3);
      return `Q${quarter} ${format(startDate, "yyyy")}`;
    case "yearly":
      return format(startDate, "yyyy");
    default:
      return `${format(startDate, "MMM dd, yyyy")} - ${format(
        endDate,
        "MMM dd, yyyy"
      )}`;
  }
};

/**
 * Determine if a field should be formatted as currency
 */
const isCurrencyField = (fieldName, value) => {
  const lowerField = fieldName.toLowerCase();
  return (
    (/total|amount|pay|charge|price|cost/i.test(lowerField) &&
      !/mileage|hours|count|quantity/i.test(lowerField)) ||
    /mileagepay|mileagetotal|payoutmileage/i.test(lowerField) ||
    lowerField === "total" ||
    lowerField === "amount" ||
    (lowerField === "mileagetotal" && lowerField !== "totalmileage")
  );
};

/**
 * Format cell value based on type
 */
const formatCellValue = (value, fieldName) => {
  if (value === null || value === undefined) return "-";

  // Handle objects (like populated driver/employee)
  if (typeof value === "object" && !Array.isArray(value) && value !== null) {
    if (value.firstName && value.lastName) {
      return `${value.firstName} ${value.lastName}`;
    }
    if (value.fullName) return value.fullName;
    if (value.name) return value.name;
    if (value.email) return value.email;
    return "[Object]";
  }

  // Handle arrays
  if (Array.isArray(value)) {
    return value.length > 0
      ? `${value.length} item${value.length !== 1 ? "s" : ""}`
      : "-";
  }

  // Handle dates
  if (
    value instanceof Date ||
    (typeof value === "string" && !isNaN(Date.parse(value)))
  ) {
    try {
      return format(new Date(value), "MMM dd, yyyy");
    } catch {
      return String(value);
    }
  }

  // Handle numbers
  if (typeof value === "number") {
    if (isCurrencyField(fieldName, value)) {
      return formatCurrency(value);
    }
    return formatNumber(value);
  }

  return String(value);
};

/**
 * Extract summary data from report summary
 */
const extractSummaryData = (summary, modelName) => {
  if (!summary) return { totals: [], averages: [] };

  const totals = [];
  const averages = [];

  // Get all sum fields
  Object.entries(summary).forEach(([key, value]) => {
    if (key.endsWith("Sum") && typeof value === "number") {
      const fieldName = key.replace("Sum", "");
      const label = formatColumnHeader(fieldName);
      const isCurrency = isCurrencyField(fieldName, value);

      totals.push({
        label,
        value: isCurrency ? formatCurrency(value) : formatNumber(value),
        isCurrency,
        fieldName,
      });
    }
  });

  // Get all average fields
  Object.entries(summary).forEach(([key, value]) => {
    if (key.endsWith("Avg") && typeof value === "number") {
      const fieldName = key.replace("Avg", "");
      const label = formatColumnHeader(fieldName);
      const isCurrency = isCurrencyField(fieldName, value);

      averages.push({
        label,
        value: isCurrency
          ? formatCurrency(value)
          : typeof value === "number"
          ? value.toFixed(2)
          : value,
        isCurrency,
        fieldName,
      });
    }
  });

  // Sort totals by importance (currency first, then alphabetically)
  totals.sort((a, b) => {
    if (a.isCurrency !== b.isCurrency) return b.isCurrency ? 1 : -1;
    return a.label.localeCompare(b.label);
  });

  return { totals: totals.slice(0, 8), averages: averages.slice(0, 6) };
};

/**
 * Format column header
 */
const formatColumnHeader = (key) => {
  return key
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (str) => str.toUpperCase())
    .replace(/\b\w/g, (str) => str.toUpperCase())
    .replace(/\s+/g, " ")
    .trim();
};

/**
 * Generate PDF for report using ReportLayout component
 * @param {Object} options - PDF generation options
 * @param {Array} options.data - Report data
 * @param {Object} options.summary - Report summary statistics
 * @param {string} options.reportName - Name of the report
 * @param {Object} options.reportConfig - Report configuration
 * @param {Object} options.reportMetadata - Report metadata (date range, etc.)
 * @param {Object} options.parentCompany - Parent company information
 * @param {Object} [options.analytics] - Invoice analytics block, rendered on invoice PDFs
 */
export const generateReportPDF = async ({
  data = [],
  summary,
  reportName = "Report",
  reportConfig = {},
  reportMetadata = {},
  parentCompany = {},
  hasMultipleEmployees = false,
  hasMultipleClients = false,
  analytics = null,
}) => {
  // Debug logs to see what's being passed
  // console.log("=== generateReportPDF called ===");
  // console.log("data length:", data?.length);
  // console.log("summary:", summary);
  // console.log("summary keys:", summary ? Object.keys(summary) : "no summary");
  // console.log("reportConfig:", reportConfig);
  // console.log("reportMetadata:", reportMetadata);
  // console.log("parentCompany:", parentCompany);

  // Create a temporary container for the ReportLayout component
  const container = document.createElement("div");
  container.style.position = "absolute";
  container.style.left = "-9999px";
  container.style.top = "-9999px";
  document.body.appendChild(container);

  const root = createRoot(container);

  return new Promise((resolve, reject) => {
    try {
      let isResolved = false;

      const handleComplete = () => {
        if (!isResolved) {
          isResolved = true;
          setTimeout(() => {
            root.unmount();
            if (document.body.contains(container)) {
              document.body.removeChild(container);
            }
            resolve();
          }, 100);
        }
      };

      // Render the ReportLayout component
      root.render(
        <ReportLayout
          data={data}
          summary={summary}
          reportName={reportName}
          reportConfig={reportConfig}
          reportMetadata={reportMetadata}
          parentCompany={parentCompany}
          hasMultipleEmployees={hasMultipleEmployees}
          hasMultipleClients={hasMultipleClients}
          analytics={analytics}
          download={true}
          onComplete={handleComplete}
        >
          <div>{/* Layout component will handle the download trigger */}</div>
        </ReportLayout>
      );

      // Timeout after 15 seconds to prevent hanging
      setTimeout(() => {
        if (!isResolved) {
          isResolved = true;
          root.unmount();
          if (document.body.contains(container)) {
            document.body.removeChild(container);
          }
          reject(new Error("PDF generation timeout"));
        }
      }, 15000);
    } catch (error) {
      root.unmount();
      if (document.body.contains(container)) {
        document.body.removeChild(container);
      }
      reject(error);
    }
  });
};

/**
 * Legacy generateReportPDF function - kept for backward compatibility
 * This is now a wrapper that uses the new ReportLayout approach
 */
export const generateReportPDFLegacy = async ({
  data = [],
  summary,
  reportName = "Report",
  reportConfig = {},
  reportMetadata = {},
}) => {
  const pdf = new jsPDF({
    orientation: "portrait",
    unit: "mm",
    format: "a4",
  });

  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margin = 15;
  let yPosition = margin;

  // Header Section
  pdf.setFillColor(59, 130, 246); // Blue color
  pdf.rect(0, 0, pageWidth, 40, "F");

  pdf.setTextColor(255, 255, 255);
  pdf.setFontSize(20);
  pdf.setFont("helvetica", "bold");
  pdf.text(reportName, margin, 20);

  pdf.setFontSize(10);
  pdf.setFont("helvetica", "normal");
  pdf.text(
    `${summary?.totalRecords || data.length} record${
      (summary?.totalRecords || data.length) !== 1 ? "s" : ""
    }`,
    margin,
    30
  );

  pdf.setTextColor(0, 0, 0);
  yPosition = 45;

  // Report Info Section
  pdf.setFontSize(10);
  pdf.setFont("helvetica", "normal");

  const infoLines = [
    `Model: ${reportConfig?.modelName || "N/A"}`,
    `Date Range: ${formatDateRange(reportMetadata)}`,
    `Generated: ${format(new Date(), "MMM dd, yyyy HH:mm")}`,
  ];

  infoLines.forEach((line, index) => {
    pdf.text(line, margin, yPosition + index * 5);
  });

  yPosition += infoLines.length * 5 + 5;

  // Summary Section - Totals
  const { totals, averages } = extractSummaryData(
    summary,
    reportConfig?.modelName
  );

  if (totals.length > 0) {
    pdf.setFontSize(12);
    pdf.setFont("helvetica", "bold");
    pdf.setTextColor(0, 0, 0);
    pdf.text("Totals", margin, yPosition);
    yPosition += 7;

    // Create summary table
    const summaryData = totals.map(({ label, value }) => [label, value]);

    pdf.autoTable({
      startY: yPosition,
      head: [["Metric", "Value"]],
      body: summaryData,
      theme: "striped",
      headStyles: {
        fillColor: [249, 250, 251],
        textColor: [0, 0, 0],
        fontStyle: "bold",
      },
      styles: {
        fontSize: 9,
        cellPadding: 3,
      },
      columnStyles: {
        0: { cellWidth: 60 },
        1: { cellWidth: 40, halign: "right" },
      },
      margin: { left: margin, right: margin },
    });

    yPosition = pdf.lastAutoTable.finalY + 10;
  }

  // Summary Section - Averages
  if (averages.length > 0) {
    pdf.setFontSize(12);
    pdf.setFont("helvetica", "bold");
    pdf.text("Averages", margin, yPosition);
    yPosition += 7;

    const avgData = averages.map(({ label, value }) => [label, value]);

    pdf.autoTable({
      startY: yPosition,
      head: [["Metric", "Average"]],
      body: avgData,
      theme: "striped",
      headStyles: {
        fillColor: [249, 250, 251],
        textColor: [0, 0, 0],
        fontStyle: "bold",
      },
      styles: {
        fontSize: 9,
        cellPadding: 3,
      },
      columnStyles: {
        0: { cellWidth: 60 },
        1: { cellWidth: 40, halign: "right" },
      },
      margin: { left: margin, right: margin },
    });

    yPosition = pdf.lastAutoTable.finalY + 10;
  }

  // Data Table Section
  if (data && data.length > 0) {
    // Get column headers from first row
    const firstRow = data[0];
    const columns = Object.keys(firstRow).filter(
      (key) => !["__v", "_id"].includes(key) && !key.startsWith("_")
    );

    // Limit columns to prevent overflow
    const maxColumns = 8;
    const selectedColumns = columns.slice(0, maxColumns);

    // Prepare table data
    const tableData = data.map((row) =>
      selectedColumns.map((col) => formatCellValue(row[col], col))
    );

    // Generate headers
    const headers = selectedColumns.map((col) => formatColumnHeader(col));

    pdf.setFontSize(12);
    pdf.setFont("helvetica", "bold");
    pdf.text("Report Data", margin, yPosition);
    yPosition += 7;

    pdf.autoTable({
      startY: yPosition,
      head: [headers],
      body: tableData,
      theme: "striped",
      headStyles: {
        fillColor: [59, 130, 246],
        textColor: [255, 255, 255],
        fontStyle: "bold",
        fontSize: 9,
      },
      bodyStyles: {
        fontSize: 8,
        cellPadding: 2,
      },
      styles: {
        cellPadding: 2,
        overflow: "linebreak",
        cellWidth: "wrap",
      },
      margin: { left: margin, right: margin },
      showHead: "everyPage",
      alternateRowStyles: {
        fillColor: [249, 250, 251],
      },
    });
  }

  // Add page numbers
  const totalPages = pdf.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    pdf.setPage(i);
    pdf.setFontSize(8);
    pdf.setTextColor(128, 128, 128);
    pdf.text(
      `Page ${i} of ${totalPages}`,
      pageWidth - margin - 20,
      pageHeight - 10
    );
  }

  return pdf;
};

/**
 * Download report as PDF using ReportLayout component
 */
export const downloadReportPDF = async (options) => {
  try {
    await generateReportPDF(options);
  } catch (error) {
    console.error("Error generating PDF:", error);
    throw error;
  }
};
